import {
  CheckCircle,
  FileText,
  FolderOpen,
  FolderTree,
  HardDrive,
  Eye,
  ArrowRight,
  Copy,
  Check,
} from "lucide-react";
import { useState } from "react";
import type { DocumentUploadResponse, StorageTreeNode } from "@/types/document";

interface UploadSuccessViewProps {
  data: DocumentUploadResponse;
  onViewDocument: () => void;
  onUploadAnother: () => void;
}

const NODE_CONFIG: Record<
  StorageTreeNode["tipe"],
  { icon: typeof FolderOpen; color: string; bgColor: string; label: string }
> = {
  root: {
    icon: HardDrive,
    color: "text-gray-700",
    bgColor: "bg-gray-100",
    label: "Storage Root",
  },
  jenis_pnbp: {
    icon: FolderOpen,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    label: "Jenis PNBP",
  },
  siklus_keuangan: {
    icon: FolderOpen,
    color: "text-emerald-700",
    bgColor: "bg-emerald-50",
    label: "Siklus Keuangan",
  },
  bentuk_dokumen: {
    icon: FolderOpen,
    color: "text-purple-700",
    bgColor: "bg-purple-50",
    label: "Bentuk Dokumen",
  },
  file: {
    icon: FileText,
    color: "text-amber-700",
    bgColor: "bg-amber-50",
    label: "File",
  },
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function TreeNode({
  node,
  depth,
  isLast,
  fileSize,
  versi,
}: {
  node: StorageTreeNode;
  depth: number;
  isLast: boolean;
  fileSize?: number;
  versi?: number;
}) {
  const config = NODE_CONFIG[node.tipe];
  const Icon = config.icon;

  return (
    <div className="flex items-start">
      {/* Tree lines */}
      <div className="flex items-center flex-shrink-0" style={{ width: depth * 28 }}>
        {Array.from({ length: depth }).map((_, i) => (
          <div key={i} className="w-7 flex justify-center">
            {i < depth - 1 ? (
              <div className="w-px h-10 bg-gray-300" />
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-px h-4 bg-gray-300" />
                <div className="flex items-center">
                  <div className="w-px h-0" />
                  <div className="w-3.5 h-px bg-gray-300" />
                </div>
                {!isLast && <div className="w-px h-4 bg-gray-300" />}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Node content */}
      <div
        className={`flex items-center gap-2.5 py-1.5 px-3 rounded-lg ${config.bgColor} my-0.5 flex-1 min-w-0`}
      >
        <Icon className={`h-4 w-4 flex-shrink-0 ${config.color}`} />
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`font-mono text-xs font-semibold ${config.color}`}>
              {node.kode}
            </span>
            {node.tipe !== "root" && (
              <span className="text-sm text-gray-700 truncate">{node.nama}</span>
            )}
          </div>
          {node.tipe === "file" && fileSize !== undefined && (
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-xs text-gray-500">
                {formatFileSize(fileSize)}
              </span>
              {versi !== undefined && (
                <span className="text-xs text-gray-500">
                  Versi {versi}
                </span>
              )}
            </div>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-wider text-gray-400 flex-shrink-0">
          {config.label}
        </span>
      </div>
    </div>
  );
}

export default function UploadSuccessView({
  data,
  onViewDocument,
  onUploadAnother,
}: UploadSuccessViewProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyHash = () => {
    navigator.clipboard.writeText(data.file_hash);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Success header */}
      <div className="flex items-center gap-3 rounded-lg bg-green-50 border border-green-200 p-4">
        <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-green-800">Dokumen Berhasil Diunggah</h3>
          <p className="text-sm text-green-700 mt-0.5">{data.judul}</p>
        </div>
      </div>

      {/* Storage tree */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
          <FolderTree className="h-4 w-4 text-gray-600" />
          <h4 className="text-sm font-semibold text-gray-700">Lokasi Penyimpanan</h4>
        </div>
        <div className="p-4">
          {data.storage_tree.map((node, index) => (
            <TreeNode
              key={`${node.tipe}-${node.kode}`}
              node={node}
              depth={index}
              isLast={index === data.storage_tree.length - 1}
              fileSize={node.tipe === "file" ? data.file_size : undefined}
              versi={node.tipe === "file" ? data.versi_ke : undefined}
            />
          ))}
        </div>

        {/* Path summary */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-500 font-mono">
            pnbp/{data.jenis_pnbp_kode}/{data.siklus_keuangan_kode}/{data.bentuk_dokumen_kode}/{data.file_name}
          </p>
        </div>
      </div>

      {/* File details */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
          <FileText className="h-4 w-4 text-gray-600" />
          <h4 className="text-sm font-semibold text-gray-700">Detail File</h4>
        </div>
        <div className="divide-y divide-gray-100">
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm text-gray-500">Nama File</span>
            <span className="text-sm font-medium text-gray-900">{data.file_name}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm text-gray-500">Ukuran</span>
            <span className="text-sm font-medium text-gray-900">
              {formatFileSize(data.file_size)}
            </span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm text-gray-500">Tipe</span>
            <span className="text-sm font-medium text-gray-900">{data.mime_type}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm text-gray-500">Versi</span>
            <span className="text-sm font-medium text-gray-900">v{data.versi_ke}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm text-gray-500">SHA-256</span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-mono text-gray-600 max-w-[200px] truncate">
                {data.file_hash}
              </span>
              <button
                onClick={handleCopyHash}
                className="p-1 rounded hover:bg-gray-100 transition-colors"
                title="Salin hash"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button
          onClick={onUploadAnother}
          className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Unggah Dokumen Lain
        </button>
        <button
          onClick={onViewDocument}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          <Eye className="h-4 w-4" />
          Lihat Dokumen
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
